/**
 * NATIVE GLASS — the frost recipe for the DS surfaces that float OVER PAGE
 * CONTENT on React Native (Toast, Tooltip / Popover).
 *
 * `expo-blur`'s `intensity` is NOT a blur radius, and that is the whole reason
 * this constant exists. iOS applies it as
 * `UIViewPropertyAnimator.fractionComplete` over a `UIVisualEffectView`
 * (expo-blur `ios/BlurEffectView.swift`) — a 0-100 fraction of how far the
 * chosen tint style's FULL effect is applied. It cannot be derived from the web
 * `backdrop-blur-[Npx]` value, which is a real gaussian radius.
 *
 * The DS derived it anyway, at 3× (web 6px → 18, web 4px → 12). A legacy
 * `light` / `dark` effect at fraction 0.18 frosts by roughly a point:
 * convincing over a plain surface, invisible over real content — which is the
 * defect Design reported on toasts and tooltips. A 70%-translucent card with no
 * effective blur behind it is not glass, it is a dimmed window: page text reads
 * straight through it.
 *
 * The app's approved glass is the Home top scrim (`SCRIM_BLUR_INTENSITY` — the
 * reference Design pointed at, "the blur the home-screen header uses, minus the
 * fading effects"): intensity 30 over an ultra-thin MATERIAL. The material
 * styles carry far less tint of their own than the legacy pair, so the DS's own
 * translucent fill stays the thing that colours the surface and the blur only
 * supplies the frost.
 *
 * ANDROID CAN frost, but only where the surface can nominate something to
 * sample — expo-blur's Android backend blurs by re-drawing a view tree you
 * hand it as `blurTarget`, so it needs a `<GlassBlurTarget>` around the app's
 * screens and it needs to sit OUTSIDE that tree. Bottom sheets qualify (they
 * are `@gorhom/bottom-sheet` portals in the app's own window). Anchored menus
 * present inside a React Native `Modal` and DO frost too: the window boundary
 * was assumed to block it, but Dimezis blurs by DRAWING the target's view tree
 * into its own canvas, and a view in another window is still drawable
 * (re-measured on a Pixel 10 emulator, API 37 — the menu goes from 2.9% to
 * 53.6% soft content with the frost on). Tooltips and popovers are the same
 * surface and frost too; `OverlayGlass` owns that recipe for all three.
 *
 * TOAST is the one overlay that genuinely cannot, and for a different reason
 * that still holds: a toast renders INSIDE the `GlassBlurTarget`, so frosting
 * it would sample a tree it is part of — a render-node cycle that crashes the
 * app (`canFrostOnAndroid`). Being in a `Modal` was never the barrier; being in
 * the SAMPLED TREE is.
 *
 * Surfaces that do not frost — that, SDK < 31, or a missing `GlassBlurTarget`
 * — fall back to an opaque-enough fill instead: see
 * `OVERLAY_FILL_CLASS_ANDROID` (Tooltip / Popover / DropdownMenu) and
 * `ToastGlassBackground.native` (Toast). `components/Glass` is the ONE layer
 * that resolves all of this; new glass surfaces should use it rather than
 * re-deciding per component.
 *
 * Plain `.ts` with no `expo-blur` import — shared has no RN runtime in its own
 * typecheck, so the tint type is restated as a literal union below.
 */
export const GLASS_BLUR_INTENSITY = 30;

/**
 * CHROME frost — the strength for small chrome surfaces that only have to lift
 * their own label off whatever is behind them: Button's glass variants, glass
 * Badges, the anchored menus, SegmentedControl's track.
 *
 * Deliberately lighter than {@link GLASS_BLUR_INTENSITY}, which is for panels
 * that must hide a whole page behind them. Named because four components had
 * this number copy-pasted into them.
 */
export const CHROME_GLASS_INTENSITY = 18;

/** The `expo-blur` tints this recipe uses, as literals (see above). */
export type GlassBlurTint =
  | 'systemUltraThinMaterialLight'
  | 'systemUltraThinMaterialDark';

/**
 * Frost tint for the current theme — an ultra-thin material, never the legacy
 * `light` / `dark` pair: those pay for their blur with a heavy white / black
 * wash that fights the translucent variant fill sitting on top of it.
 */
export function glassBlurTint(isDark: boolean): GlassBlurTint {
  return isDark ? 'systemUltraThinMaterialDark' : 'systemUltraThinMaterialLight';
}

/**
 * ANDROID — the blur RADIUS ceiling, in px.
 *
 * expo-blur's Android view reads `intensity` as a radius (divided by
 * `blurReductionFactor`, which `GlassBackground.native` pins to 1 so the DS's
 * one number means one thing). The Dimezis backend behind it is built around
 * the platform blur's historical 25px ceiling, so anything above this is at
 * best ignored and at worst throws.
 *
 * Figma asks for 27 on the sheet; 25 is as close as Android goes, and the two
 * are indistinguishable at this scale.
 */
export const ANDROID_MAX_BLUR_RADIUS_PX = 25;

/**
 * ANDROID — the API level from which the frost is cheap enough to ship.
 *
 * From SDK 31 the Dimezis backend blurs with `RenderEffect`, which is GPU
 * work; below it, it falls back to re-drawing into a bitmap on every frame,
 * which is the "costly/janky on a surface this size" the DS's glass surfaces
 * have been avoiding since. The app's `minSdkVersion` is 28, so the older path
 * is reachable and deliberately not taken — those devices get the fill.
 */
export const ANDROID_MIN_BLUR_SDK = 31;

/**
 * Whether an Android surface can actually frost right now: new enough OS, an
 * app that mounted a `<GlassBlurTarget>` for it to sample, and a surface that
 * is NOT itself inside that target.
 *
 * The last condition is a crash guard, not a look. Dimezis blurs by drawing
 * the target's render node into the blur view's own; a blur view INSIDE the
 * target turns that into a cycle, and the RenderThread recurses until its
 * stack overflows — `SIGSEGV` in `libhwui` (`prepareTreeImpl`,
 * `DamageAccumulator::pushTransform`, `computeTransformImpl` on top of 512
 * identical frames), killing the app the frame the surface first paints. See
 * `GlassBlurTargetScope` for how "inside" is known.
 *
 * Pure so it is testable — `.native.tsx` is excluded from this package's
 * typecheck, so the decision lives here rather than in the shim that applies
 * it.
 */
export function canFrostOnAndroid(
  sdk: number,
  hasBlurTarget: boolean,
  insideBlurTarget: boolean,
): boolean {
  return hasBlurTarget && !insideBlurTarget && sdk >= ANDROID_MIN_BLUR_SDK;
}

/** The DS intensity as an Android blur radius, clamped to what the OS takes. */
export function androidBlurRadius(intensity: number): number {
  return Math.max(0, Math.min(intensity, ANDROID_MAX_BLUR_RADIUS_PX));
}

/**
 * ANDROID ONLY — the (scrim, fill) pair a frostable surface frosts through,
 * tuned as ONE thing because they trade against each other.
 *
 * THE REUSABLE HALF of the sheet's glass. Any DS surface that can frost on
 * Android (see {@link canFrostOnAndroid}) and floats over content something
 * else has DIMMED wants exactly these two numbers, because the cause is not
 * the sheet — it is that Android's blur samples the `GlassBlurTarget` (the
 * SCREENS) and therefore never sees a dim drawn in a portal above them.
 *
 * Both are LOW on purpose. The frost only carries as much fuzz as the page
 * gets to contribute: the fill washes it out multiplicatively, and the scrim —
 * a flat black layer — washes it out again, so the shipped `0.70` fill under
 * an exact `0.50` backdrop re-paint left the blurred page just
 * `0.3 × 0.5 = 15%` of the result and the card read as a tinted slab with no
 * fuzziness in it at all.
 *
 * iOS gets away with far more fill because `systemUltraThinMaterial` is a
 * VIBRANCY material — it lifts the contrast of whatever it samples. Dimezis
 * has no equivalent, so on Android the legibility of the blur has to be bought
 * with alpha instead, and that is the whole reason these two numbers are
 * Android-only rather than the shared the shared 70% surface fill.
 *
 * At `(0.25, 0.20)` the page contributes ~60% — enough that what is behind the
 * card reads as recognisable-but-unreadable shapes, the way frosted glass
 * should — while the card still lands near ~205 against the ~122 backdrop, so
 * it sits ON the dim rather than fighting it.
 *
 * Tune them as a PAIR, and watch BOTH ends: raising either flattens the frost;
 * dropping the scrim too far lets the card drift back up toward the undimmed
 * page and clash with the backdrop again.
 */
export const ANDROID_FROST_SCRIM_OPACITY = 0.2;

/** The dim itself. gorhom's backdrop is black; so is every scrim standing in for one. */
export const ANDROID_FROST_SCRIM_COLOR = '#000';
export const ANDROID_FROST_FILL_CLASS = 'bg-secondary-100/25';
